import { usePoiRead } from "./usePoiRead";
import { useAppSelector } from "app/hooks";
import Button from "shared/components/Button";
import { ReactComponent as CrossSVG } from "assets/icons/cross.svg";
import DataItem from "shared/components/DataItem";
import StatIcon from "shared/components/StatIcon";
import { definePoiKPIs } from "shared/components/StatIcon/statIconModels";
import React, { useCallback, useMemo, useState } from "react";
import Modal from "shared/components/Modal";
import PortalledContent from "shared/components/PortalledContent";
import { notNullNorUndefined } from "shared/helpers/notNullNorUndefined";
import PoiCarousel, { PoiCarouselProps } from "./PoiCarousel";
import PoiImageZoom from "./PoiImageZoom";
import Panel from "shared/components/Panel";
import useMedia from "use-media";
import { MOBILE_VIEW_BREAKPOINT } from "shared/helpers/mediaQueries";

const PORTAL_ID = "POIReactContainer";

const KPIs = definePoiKPIs([
  // Overall good health
  "poi_cover",
  // Good health (other)
  "poi_gai",
  "poi_count",
  "poi_count_per_area",
  "poi_ear_count",
  "poi_ear_count_per_area",
  "poi_growth_stage_aab",
  "poi_growth_stage_bbch",
  // Overual bad health
  "poi_unhealthy",
  "poi_weeds",
  "poi_bugs",
  "poi_weed_count",
  "poi_weed_count_per_area",
  // Bad health (other)
  "poi_flowering",
  "poi_ripening",
  "yellow_leaves",
  "purple_leaves",
  //
] as const);

interface PoiReadProps {}

const PoiRead: React.FC<PoiReadProps> = () => {
  const largerThanMobileView = useMedia(MOBILE_VIEW_BREAKPOINT);
  const targetPoiId = usePoiRead();
  const poiDetails = useAppSelector((state) =>
    state.panorama.currentFieldDetails?.scoutPoints.find(
      (poi) => poi.name === targetPoiId
    )
  );
  const shortDate = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.shortdate
  );
  const cropType = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.crop_type
  );
  const imperialUnits = useAppSelector((state) => state.panorama.imperialUnits);

  /**
   * Index of the selected image in the carousel
   */
  const [selectedItem, setSelectedItem] = useState<number | null>(null);

  // Close the POI panel
  const closePanel = useCallback(() => {
    window.krpano.call("close_hotspots();");
  }, []);

  // Close the detailed image modal
  const closeModal = useCallback(() => setSelectedItem(null), []);

  const poiImages: PoiCarouselProps["images"] = useMemo(() => {
    if (poiDetails) {
      if (poiDetails.poi_urls && poiDetails.poi_urls.length > 0) {
        return poiDetails.poi_urls;
      } else {
        return [poiDetails.poi_url];
      }
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPoiId]);

  const titleText = poiDetails
    ? poiDetails.name.toLowerCase().replace("poi", "Scout Point")
    : "Scout Point";

  // NOTE: `key` prop in Fragment
  // REASON: Change to `targetPoiId` was done outside of React so did not cause
  // a re-render, therefore the `key` prop is needed to force that behaviour.
  const poiReadInner = (
    <React.Fragment key={`POI_CONTENT_${targetPoiId}`}>
      <div className="KrpanoReactInner PoiContent">
        {/* Images & Close Button */}
        <PoiCarousel
          images={poiImages}
          height={200}
          width={400}
          className="PoiImage"
          onClickItem={(index) => {
            // Used to open _that_ image in the modal
            setSelectedItem(index);
          }}
        />
        {/* SEE: POI_READ_FULLSCREEN */}
        {largerThanMobileView && (
          <Button
            colour="Theme"
            additionalClassNames="Cross NoBorder"
            onClick={closePanel}
            children={
              <CrossSVG
                height={16}
                width={16}
                role="img"
                aria-label="Close Panel"
                title="Close Panel"
              />
            }
          />
        )}
        <div className="Details DisplayFlex FlexColumn MediumGap">
          {/* Top Section - START */}
          {/* SEE: POI_READ_FULLSCREEN */}
          {largerThanMobileView ? <h2>{titleText}</h2> : <div />}
          <div className="DisplayFlex FlexColumn VSmallGap">
            <DataItem left="Crop type" middle={cropType || "No crop"} />
            {/*
            NOTE: Scout Date comes from pano date, not when the image was taken
            We assume they were done together because there's no indication
            otherwise
            */}
            <DataItem left="Scout date" middle={shortDate || "No date"} />
          </div>
          {/* Top Section - END */}
          <hr />
          {/* Measurements - START */}
          <h3>Measurements</h3>
          <div className="MeasurementsParent DisplayFlex FlexColumn">
            {poiDetails ? (
              <>
                {KPIs.map((kpi, index) => (
                  <StatIcon
                    key={`KPI_${index}`}
                    targetObject="poi"
                    targetKey={kpi}
                    // NOTE: As `any` is required here until we fix the
                    // `unknown` types in the API
                    value={poiDetails[kpi] as any}
                    imperialUnits={imperialUnits}
                    iconSize={24}
                    parentClassNames="DisplayFlex AlignCentre SmallGap FitWidth"
                    viewType="dataItem"
                    tooltipTarget="poiDetailsPanel"
                    tooltipContentKey="description"
                    tooltipPlace="bottom"
                  />
                ))}
                <h5 className="ImportantSubText NoDetailsMessage">
                  No measurements available
                </h5>
              </>
            ) : (
              <h5 className="ImportantSubText">
                There are no Scout Point details at this time
              </h5>
            )}
          </div>
          {/* Measurements - END */}
        </div>
      </div>
      {/* Nested `PortalledContent` for full-size view of POI Image */}
      <PortalledContent
        children={
          <Modal
            open={notNullNorUndefined(selectedItem)}
            closeModal={closeModal}
            title="Scout Point Image"
            children={
              <div className="PoiImageFullScreenParent">
                {/* FULLSCREEN_POI Option 1: POI Carousel to show all images as large as possible */}
                {/* <PoiCarousel
                  images={poiImages}
                  height="100%"
                  width="auto"
                  selectedItem={selectedItem ?? 0}
                /> */}
                {/* FULLSCREEN_POI Option 2: Single POI Image zoomed in with library */}
                <PoiImageZoom
                  imageUrl={
                    notNullNorUndefined(selectedItem)
                      ? poiImages[selectedItem]
                      : undefined
                  }
                />
                {/* IDEA: Combine options to get a carousel of zoomable images */}
              </div>
            }
            hideFooterButtons
            fullSize
            // FULLSCREEN_POI Option 1:
            // paddingClass="ReducedPadding"
            //
            // FULLSCREEN_POI Option 2:
            paddingClass="NoPadding"
            invertColours
            //
          />
        }
      />
    </React.Fragment>
  );

  // Small devices will open a bottom panel
  // SEE: POI_READ_FULLSCREEN
  if (!largerThanMobileView) {
    return (
      <Panel
        showPanel={targetPoiId !== null}
        direction="bottom"
        headerBorder="BorderBottom"
        closePanel={closePanel}
        underlayClass="FaintUnderlay"
        panelSize="full"
        // scrollClass="ScrollAllInner"
        scrollClass="ScrollContent"
        headerElement={<h2 className="h2Small">{titleText}</h2>}
      >
        {poiReadInner}
      </Panel>
    );
  }

  // NOTE: Only return null here if the `targetPoiId` is `null`
  // If `poiDetails` is `undefined`, we will show placeholder UI
  //
  // NOTE: This is here (and not above Panel) to allow it to control it's own
  // mount/unmount animations
  if (targetPoiId === null) {
    return null;
  }
  // Normal devices will open the POI in a portal so it moves inside krpano
  return <PortalledContent targetPortal={PORTAL_ID} children={poiReadInner} />;
};

export default PoiRead;
